import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Bell,
  Bot,
  Database,
  GitBranch,
  KeyRound,
  LockKeyhole,
  Moon,
  Save,
  Settings2,
  ShieldCheck,
  Sun,
  UserRoundCog,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { CapabilityStatus, CAPABILITY_STATUS } from "../components/CapabilityStatus";
import { useToasts } from "../components/Toasts";
import "./SettingsPage.css";

const AVAILABLE_PROVIDERS = [
  { key: "emergent", label: "Emergent", disabled: false },
  { key: "openai", label: "OpenAI (coming soon)", disabled: true },
  { key: "anthropic", label: "Anthropic (coming soon)", disabled: true },
  { key: "gemini", label: "Gemini (coming soon)", disabled: true },
];

const MODEL_OPTIONS = {
  emergent: ["gpt-5.4", "gpt-5", "gpt-5-mini", "claude-sonnet-4-6", "gemini-3.1-pro-preview"],
};

const SETTINGS_CARDS = [
  { key: "integrations", title: "Integrations", copy: "GitHub credentials, repository connections, and external sources of truth.", icon: GitBranch, status: "live", action: "Open integrations" },
  { key: "ai-agents", title: "AI & Agents", copy: "Provider, model, active state, and department-level agent configuration.", icon: Bot, status: "live", action: "Configure agents" },
  { key: "users-access", title: "Users & Access", copy: "Role enforcement exists; a complete user-management settings surface is still being built.", icon: UserRoundCog, status: "in_development", action: "View development scope" },
  { key: "notifications", title: "Notifications", copy: "Attention alerts, approvals, email, text, and future communication preferences.", icon: Bell, status: "planned", action: "View planned scope" },
  { key: "appearance", title: "Appearance", copy: "Switch between the standard dark console and high-contrast sunlight mode.", icon: Sun, status: "live", action: "Change theme" },
  { key: "data-sync", title: "Data & Sync", copy: "Core synchronization exists; broader source health and refresh controls are still being built.", icon: Database, status: "in_development", action: "View development scope" },
  { key: "security", title: "Security & Audit", copy: "Credential status, protected integrations, audit trail, and ledger verification.", icon: ShieldCheck, status: "live", action: "Open audit trail" },
  { key: "general", title: "General", copy: "LincWerks defaults, organization identity, and platform-wide behavior.", icon: Settings2, status: "in_development", action: "View development scope" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const shell = useOutletContext() || {};
  const theme = shell.theme || document.documentElement.dataset.theme || "dark";
  const setTheme = shell.setTheme;
  const push = useToasts((s) => s.push);
  const canWrite = user?.role === "director" || user?.role === "admin";
  const [departments, setDepartments] = useState([]);
  const [agentsByKey, setAgentsByKey] = useState({});
  const [saving, setSaving] = useState(null);
  const [agentConfigOpen, setAgentConfigOpen] = useState(false);
  const [scopeCard, setScopeCard] = useState(null);

  const load = async () => {
    const { data: dpts } = await api.get("/departments");
    setDepartments(dpts);
    const rs = await Promise.all(dpts.map((d) => api.get(`/departments/${d.key}/agents`).then((r) => [d.key, r.data])));
    setAgentsByKey(Object.fromEntries(rs));
  };

  useEffect(() => { load(); }, []);

  const update = async (agent, patch) => {
    setSaving(agent.id);
    try {
      const { data } = await api.patch(`/agents/${agent.id}`, patch);
      setAgentsByKey((prev) => ({ ...prev, [agent.department_key]: prev[agent.department_key].map((a) => (a.id === agent.id ? data : a)) }));
      push(`Updated ${agent.name}`, "ok");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      push(typeof detail === "string" ? detail : "Update failed", "err");
    } finally { setSaving(null); }
  };

  const activateCard = (key) => {
    setScopeCard(null);
    if (key === "integrations") { navigate("/commandlinc/integrations"); return; }
    if (key === "security") { navigate("/commandlinc/ledger"); return; }
    if (key === "ai-agents") {
      setAgentConfigOpen(true);
      requestAnimationFrame(() => document.getElementById("ai-agent-settings")?.scrollIntoView({ behavior: "smooth", block: "start" }));
      return;
    }
    if (key === "appearance") { setTheme?.(theme === "dark" ? "sunlight" : "dark"); return; }
    setScopeCard(key);
  };

  const scoped = SETTINGS_CARDS.find((card) => card.key === scopeCard);

  return (
    <>
      <TopBar title="Settings" subtitle="One control center for LincWerks configuration, integrations, access, agents, and platform behavior." />
      <section className="settings-overview" aria-label="Settings categories">
        {SETTINGS_CARDS.map((card) => {
          const Icon = card.icon;
          const isAppearance = card.key === "appearance";
          return (
            <button key={card.key} type="button" className="settings-card" onClick={() => activateCard(card.key)} data-testid={`settings-card-${card.key}`}>
              <span className="settings-card-icon"><Icon size={20} /></span>
              <span className="settings-card-copy">
                <span className="settings-card-title-row"><b>{card.title}</b><CapabilityStatus status={card.status} compact /></span>
                <small>{card.copy}</small>
                <span className="settings-card-action">{isAppearance ? (theme === "dark" ? <><Sun size={13} /> Switch to sunlight</> : <><Moon size={13} /> Switch to dark</>) : card.action}</span>
              </span>
            </button>
          );
        })}
      </section>

      {scoped ? <section className="settings-scope-panel" data-testid="settings-scope-panel"><div><span className="settings-scope-eyebrow">CAPABILITY STATUS</span><div style={{ margin: "6px 0 8px" }}><CapabilityStatus status={scoped.status} /></div><h2>{scoped.title}</h2><p>{scoped.copy}</p><p className="hint">{CAPABILITY_STATUS[scoped.status]?.copy}</p></div><button className="btn small" onClick={() => setScopeCard(null)}>Close</button></section> : null}

      <section id="ai-agent-settings" className={`panel settings-agent-panel${agentConfigOpen ? " open" : ""}`}>
        <div className="panel-head settings-agent-head"><div><h2><Bot size={15} /> AI & Agent configuration</h2><p>Provider + model per department. Ledger event on every change.</p></div><div className="settings-agent-actions">{!canWrite ? <span className="pill amber">read-only</span> : null}<button className="btn small" onClick={() => setAgentConfigOpen((value) => !value)}>{agentConfigOpen ? "Collapse" : "Open configuration"}</button></div></div>
        {agentConfigOpen ? <div className="panel-body settings-agent-body">
          {departments.length === 0 ? <EmptyState title="No departments." /> : null}
          {departments.map((d) => {
            const agents = agentsByKey[d.key] || [];
            return <div key={d.key} className="settings-department" data-testid={`settings-dept-${d.key}`}><div className="settings-department-meta"><b>{d.name}</b><span className="mono">{d.key}</span>{d.can_block_release ? <span className="pill red">release gate</span> : null}</div><div className="settings-agent-list">{agents.length === 0 ? <small className="hint">no agent</small> : agents.map((a) => <AgentRow key={a.id} agent={a} canWrite={canWrite} saving={saving === a.id} onSave={update} />)}</div></div>;
          })}
        </div> : null}
      </section>

      <section className="settings-shortcuts">
        <button className="settings-shortcut" onClick={() => navigate("/commandlinc/integrations")}><KeyRound size={15} /><span><b>GitHub & credentials</b><small>Configure or rotate repository access without knowing a hidden URL.</small></span></button>
        <button className="settings-shortcut" onClick={() => navigate("/commandlinc/ledger")}><LockKeyhole size={15} /><span><b>Security audit trail</b><small>Open the immutable activity ledger and verification surface.</small></span></button>
      </section>
    </>
  );
}

function AgentRow({ agent, canWrite, saving, onSave }) {
  const [provider, setProvider] = useState(agent.provider);
  const [model, setModel] = useState(agent.model);
  const [active, setActive] = useState(agent.active);
  const dirty = provider !== agent.provider || model !== agent.model || active !== agent.active;

  useEffect(() => { setProvider(agent.provider); setModel(agent.model); setActive(agent.active); }, [agent.provider, agent.model, agent.active]);

  return (
    <div className="settings-agent-row">
      <div className="settings-agent-name"><small className="hint">Agent</small><b>{agent.name}</b></div>
      <label><small className="hint">Provider</small><select value={provider} onChange={(e) => setProvider(e.target.value)} disabled={!canWrite} data-testid={`agent-${agent.id}-provider`}>{AVAILABLE_PROVIDERS.map((p) => <option key={p.key} value={p.key} disabled={p.disabled}>{p.label}</option>)}</select></label>
      <label><small className="hint">Model</small><select value={model} onChange={(e) => setModel(e.target.value)} disabled={!canWrite} data-testid={`agent-${agent.id}-model`}>{(MODEL_OPTIONS[provider] || [model]).map((m) => <option key={m} value={m}>{m}</option>)}{!MODEL_OPTIONS[provider]?.includes(model) ? <option value={model}>{model}</option> : null}</select></label>
      <label className="settings-active-toggle"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canWrite} data-testid={`agent-${agent.id}-active`} />active</label>
      <button className="btn primary small" disabled={!canWrite || !dirty || saving} onClick={() => onSave(agent, { provider, model, active })} data-testid={`agent-${agent.id}-save`}>{saving ? <span className="loader" /> : <><Save size={12} /> Save</>}</button>
    </div>
  );
}
